/**
 * buildCounterSavePayload — build the `CounterRequestDto` body for
 * `POST /counter/{id}` and `POST /counter/simulator/{id}` (Rem18 Task 4
 * Symfony migration of the legacy hangars saveCounter endpoints).
 *
 * Backend contract (`CounterRequestDto.php:10-35`, `CounterManager.php:87-93`):
 *   - `value`              — required, string (digits + `.` or `,`)
 *   - `comments`           — optional, string; empty allowed since backend
 *                            `a2ceb97` dropped the `Assert\NotBlank` (stored
 *                            for the audit log by `CounterManager::editCounter`)
 *   - `eventId`            — optional, number; surfaces in history
 *   - `organizationId`     — required, number; drives access check
 *   - `organizationUnique` — optional, string; fallback for the access check
 *   - `aircraftId`         — optional, number (aircraft route only)
 *   - `rate`               — optional, string; persisted to the counter row
 *   - `enableRate`         — optional, bool; persisted to the counter row
 *   - `oldValue`           — optional, string; backend history math
 *   - `timezone`           — optional, string; default 'UTC'
 *
 * Pre-Sub-4 the call site at `CheckOutScreen.js:377-386` omitted `rate` and
 * `enableRate`. `CounterManager` reads both fields and writes to the counter
 * row — omitting them clobbers the persisted values. We now propagate them
 * from the original counter row (loaded via `check-out-start`) so save
 * round-trips preserve the current rate config.
 *
 * Pure builder so the contract is testable without spinning up React /
 * Redux / API mocks (per `feedback_test_scope.md` — utility-test allow-list).
 *
 * @param {Object} counter  The original counter row from `bootstrap.counters`.
 *   Source for `oldValue`, `rate`, `enableRate`. Properties read defensively:
 *   `value`, `rate`, `enableRate`.
 * @param {{ value?: *, oldValue?: *, comments: * }} edit  User edits.
 *   - `value`    when present → becomes `payload.value`; else `payload.value = c.value`.
 *   - `oldValue` when present → becomes `payload.oldValue`; else fallback
 *     `payload.oldValue = c.value` (preserves the existing CheckOut/CheckIn
 *     contract; the Start-edit path from `ResourcePanel.MetersSection` is
 *     enabled by passing `edit.oldValue` explicitly).
 * @param {Object} ctx
 * @param {number|string} ctx.eventId
 * @param {number|string} ctx.organizationId  REQUIRED — backend rejects without it.
 * @param {string} [ctx.organizationUnique]
 * @param {number|string} [ctx.aircraftId]    Omitted for simulator route.
 * @param {boolean} [ctx.isSimulatorResource] When true, omit `aircraftId`.
 * @param {string} [ctx.timezone='UTC']
 * @returns {Object} `CounterRequestDto` body.
 */
export function buildCounterSavePayload(counter, edit, ctx) {
	const e = edit || {};
	const c = counter || {};
	const x = ctx || {};

	// `value` fallback to `c.value` lets a Start-only edit (which omits `value`)
	// send the unchanged End reading instead of an empty string. Empty-string
	// `e.value` ALSO falls through — `MetersSection` initializes the untouched
	// field's draft as `""` for a controlled `<input>`, and a blank string would
	// otherwise trigger backend `Assert\NotBlank` (`value is required`, 422).
	// CheckOut/CheckIn callers pass `e.value` explicitly with the user's number,
	// so their payload is unchanged.
	const editValueStr =
		e.value !== undefined && e.value !== null ? String(e.value).trim() : '';
	const valueSource = editValueStr !== '' ? editValueStr : c.value;

	const payload = {
		value: String(valueSource ?? ''),
		comments: String(e.comments ?? ''),
		organizationId: Number(x.organizationId),
		timezone: x.timezone || 'UTC'
	};

	if (x.eventId != null && x.eventId !== '') {
		const n = Number(x.eventId);
		if (Number.isFinite(n)) payload.eventId = n;
	}
	if (x.organizationUnique) {
		payload.organizationUnique = x.organizationUnique;
	}
	if (!x.isSimulatorResource && x.aircraftId != null && x.aircraftId !== '') {
		const n = Number(x.aircraftId);
		if (Number.isFinite(n)) payload.aircraftId = n;
	}

	// Propagate the counter row's `rate` + `enableRate` so backend doesn't
	// overwrite them with nulls. Coerce to string per DTO contract.
	if (c.rate != null && c.rate !== '') {
		payload.rate = String(c.rate);
	}
	if (c.enableRate !== undefined && c.enableRate !== null) {
		payload.enableRate = c.enableRate === true || c.enableRate === 'true' || c.enableRate === 1 || c.enableRate === '1';
	}

	// `oldValue` lets the backend detect out-of-sequence edits and write the
	// history row correctly. Coerce to string per DTO. Explicit `edit.oldValue`
	// (Start-edit path) wins; otherwise fall back to the row's current `value`
	// (the pre-edit reading — original CheckOut/CheckIn contract).
	// Trim-aware so a blank-string draft falls through to the fallback instead
	// of clobbering history with `""`.
	const editOldValueStr =
		e.oldValue !== undefined && e.oldValue !== null ? String(e.oldValue).trim() : '';
	if (editOldValueStr !== '') {
		payload.oldValue = editOldValueStr;
	} else if (c.value != null && c.value !== '') {
		payload.oldValue = String(c.value);
	}

	return payload;
}
